// Keys to scrub from Sentry events and RUM actions, lowercased to compare
// against key words.
const SENSITIVE_KEYS = new Set(
  [
    // Names
    "name",
    "firstName",
    "lastName",
    "patientName",
    "signerName",
    "caseManagerName",
    "clinicianName",
    "providerName",
    "createdBy",

    // Contact info
    "email",
    "phone",
    "phoneNumber",
    "secondaryPhoneNumber",
    "caseManagerPhone",
    "careCoordinatorEmail",
    "address",
    "street",
    "city",
    "state",
    "zip",
    "zipCode",
    "address1",
    "address2",
    "claimAddress",
    "shippingAddress",
    "stateAbbr",
    "stateName",
    "fax",

    // IDs
    "ssn",
    "socialSecurityNumber",
    "passportNumber",
    "licenseNumber",
    "memberId",
    "patientId",
    "medicalRecordNumber",
    "claimNumber",
    "primaryPolicyNumber",
    "secondaryPolicyNumber",
    "primaryGroupNumber",
    "secondaryGroupNumber",
    "careInternalId",
    "careMco",
    "openpmId",
    "externalId",
    "orderNumber",

    // Credentials
    "password",
    "secret",
    "token",
    "authToken",
    "apiKey",
    "auth",
    "authorization",

    // Financial
    "creditCard",
    "creditCardNumber",
    "cc",
    "ccnum",
    "cvv",
    "cvc",

    // Health
    "dob",
    "dateOfBirth",
    "patientDob",
    "gender",
    "diagnosis",
    "diagnoses",
    "icd10",
    "icd10Description",
    "hcpcs",
    "procedure",
    "treatment",
    "planOfCare",
    "note",
    "notes",
    "patientNotes",
    "healthInsuranceNumber",
    "dateOfAccident",
    "stateOfAccident",
    "primaryCasualDiagnosis",
    "primaryDiagnosisOther",
    "secondaryDiagnosisOther",
    "patientHeightFeet",
    "patientHeightInches",
    "patientWeight",
    "hospital",
    "hospitalName",
    "departmentName",

    // Insurance
    "insurance",
    "primaryInsurance",
    "secondaryInsurance",
    "insuranceName",
    "insuranceType",
    "primaryInsuranceType",

    // Other potentially sensitive info from GraphQL schema
    "location",
    "signer",

    // Free-text search, which carries typed patient names
    "search",
    "query",

    // Free text written by staff or a model, which can quote a patient
    "message",
    "responseText",
    "llmChosenResponse",
  ].map((key) => key.toLowerCase()),
);

// Known non-PHI debug keys that the word-run match would otherwise catch,
// compared whole after lowercasing and dropping underscores.
const ALLOWED_KEYS = new Set([
  "operationname",
  "networkerrorname",
  "stateid",
  "locationid",
  "productname",
  "includeprogressnotesummary",
  "originalmessage",
]);

const KEY_WORD = /[A-Z]+(?=[A-Z][a-z])|[A-Z]?[a-z]+|[A-Z]+|\d+/g;

// Sensitive when a run of the key's consecutive camelCase or snake_case words,
// joined and lowercased, is in SENSITIVE_KEYS: dateOfBirthQuery hits
// "dateofbirth", businessNumber does not hit "ssn".
function isSensitiveKey(key: string): boolean {
  if (ALLOWED_KEYS.has(key.replaceAll("_", "").toLowerCase())) {
    return false;
  }
  const words = (key.match(KEY_WORD) ?? []).map((word) => word.toLowerCase());
  for (let start = 0; start < words.length; start++) {
    for (let end = start + 1; end <= words.length; end++) {
      const run = words.slice(start, end).join("");
      // A plural last word counts too, so emails and shippingAddresses match.
      const spellings =
        end === words.length
          ? [run, run.replace(/s$/, ""), run.replace(/es$/, "")]
          : [run];
      if (spellings.some((spelling) => SENSITIVE_KEYS.has(spelling))) {
        return true;
      }
    }
  }
  return false;
}

// graphql-core writes the offending value into a variable coercion error.
function redactVariableValue(message: string): string {
  return message.replace(
    /(Variable '\$[^']*') got invalid value[\s\S]*/,
    "$1 got invalid value",
  );
}

// Route paths carry ids (order numbers, member keys, portal access tokens) and
// query strings carry search text. Keep a URL's host and route words: drop the
// query and fragment, and swap each path segment holding a digit (but no dot,
// so asset file names stay) for :id, the apps' route-param style.
function scrubUrls(text: string): string {
  return text.replace(/(?:https?:\/\/|(?<!\S)\/)\S*/g, (url) =>
    url
      .replace(/[?#].*/, "")
      .replace(/(?<!\/)\/[^/.]*\d[^/.]*(?=\/|$)/g, "/:id"),
  );
}

const MASK = "[REDACTED]";

export function scrubData(data: any): any {
  if (typeof data === "string") {
    return scrubUrls(redactVariableValue(data));
  }
  if (typeof data !== "object" || data === null) {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(scrubData);
  }

  const scrubbedObject: { [key: string]: any } = {};
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      if (isSensitiveKey(key)) {
        scrubbedObject[key] = MASK;
      } else {
        scrubbedObject[key] = scrubData(data[key]);
      }
    }
  }
  return scrubbedObject;
}

// Contexts Sentry fills itself describe the runtime, not the patient, and their
// name keys (os.name, browser.name) would otherwise be redacted. trace is still
// scrubbed, since its data carries URLs.
const SENTRY_CONTEXTS = new Set([
  "app",
  "browser",
  "cloud_resource",
  "culture",
  "device",
  "flags",
  "os",
  "profile",
  "react",
  "runtime",
]);

// Rewrites the parts of a Sentry error or transaction event that carry request
// data, URLs or error text.
export function scrubSentryEvent(event: {
  transaction?: string;
  extra?: Record<string, unknown>;
  breadcrumbs?: unknown[];
  request?: object;
  contexts?: object;
  spans?: unknown[];
  exception?: { values?: { value?: string }[] };
}): void {
  if (event.transaction) {
    event.transaction = scrubData(event.transaction);
  }
  if (event.extra) {
    event.extra = scrubData(event.extra);
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = scrubData(event.breadcrumbs);
  }
  if (event.request) {
    event.request = scrubData(event.request);
  }
  if (event.contexts) {
    event.contexts = Object.fromEntries(
      Object.entries(event.contexts).map(([key, context]) => [
        key,
        SENTRY_CONTEXTS.has(key) ? context : scrubData(context),
      ]),
    );
  }
  if (event.spans) {
    event.spans = scrubData(event.spans);
  }
  event.exception?.values?.forEach((exception) => {
    if (exception.value) {
      exception.value = redactVariableValue(exception.value);
    }
  });
}

// RUM collects console errors and unhandled rejections.
export function redactRumError(error: { message: string; stack?: string }) {
  error.message = redactVariableValue(error.message);
  if (error.stack) {
    error.stack = redactVariableValue(error.stack);
  }
}
